import { REPUB_CONFIG } from '@cx-web/repub-config';
import { getRepubMetaFromRepub } from '@cx-web/utils';

import { type Repub, REPUBS } from '@cx-modules/constants';

import { ErrorContent, ErrorPageStyles, ErrorWhoScored, Favicon, Fonts } from './components';
import { getErrorPageStrings } from './strings';

/**
 * Self-sufficient root error page. Renders the entire document from only
 * { repub, errorStatus, resourcesUrl } — no app loader, app API, or i18n fetch.
 * Strings are bundled, theme colours are inlined, assets resolve from the CDN base.
 */
export function RootErrorPage({
  errorStatus,
  repub,
  resourcesUrl,
}: {
  errorStatus: number | null;
  repub: Repub;
  resourcesUrl: string;
}) {
  const { repubHomeUrl: baseUrl } = getRepubMetaFromRepub(repub);
  const errorPageConfig = REPUB_CONFIG[repub]?.plus?.errorPage;

  // Redirect to external 404 page if configured (e.g. GZ → Gazzetta's 404).
  if (errorPageConfig?.redirectUrl && errorStatus === 404) {
    return (
      <html lang={REPUBS[repub]?.locale ?? 'en-GB'}>
        <head>
          <meta charSet="utf-8" />
          <meta content="noindex" name="robots" />
          <meta content={`0;url=${errorPageConfig.redirectUrl}`} httpEquiv="refresh" />
        </head>
        <body />
      </html>
    );
  }

  // Repub with a fully custom error page (e.g. WS → ErrorWhoScored).
  if (errorPageConfig?.customComponent === 'ErrorWhoScored') {
    return (
      <html lang={REPUBS[repub]?.locale ?? 'en-GB'}>
        <head>
          <meta charSet="utf-8" />
          <meta content="noindex" name="robots" />
          <meta content="width=device-width,initial-scale=1" name="viewport" />
          <Favicon repub={repub} resourcesUrl={resourcesUrl} />
        </head>
        <body>
          <ErrorWhoScored baseUrl={baseUrl} />
        </body>
      </html>
    );
  }

  const strings = getErrorPageStrings(repub);
  // us-404.png / us-500.png are shared error artwork across all repubs (no per-repub variants exist).
  const statusLogoPng = errorStatus === 404 ? 'us-404.png' : 'us-500.png';

  return (
    <html lang={REPUBS[repub]?.locale ?? 'en-GB'}>
      <head>
        <meta charSet="utf-8" />
        <meta content="noindex" name="robots" />
        <meta content="width=device-width,initial-scale=1" name="viewport" />
        <Favicon repub={repub} resourcesUrl={resourcesUrl} />
        <Fonts />
        <ErrorPageStyles repub={repub} />
      </head>
      <body>
        <ErrorContent baseUrl={baseUrl} resourcesUrl={resourcesUrl} statusLogoPng={statusLogoPng} strings={strings} />
      </body>
    </html>
  );
}
